import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type HubHoverTableColumn<Row> = {
  id: string;
  header: ReactNode;
  align?: "left" | "right";
  headerClassName?: string;
  cellClassName?: string;
  /** Prefer wrap for long labels; nowrap for short ids and numbers. */
  overflow?: "wrap" | "nowrap";
  cell: (row: Row) => ReactNode;
};

export type HubHoverTableProps<Row> = {
  columns: readonly HubHoverTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string | number;
  caption?: ReactNode;
  /** Soft width cap for the shrink-wrapped hint table. */
  className?: string;
  tableClassName?: string;
  footer?: ReactNode;
};

/**
 * Dense peek table for hover hints. Shrink-wraps, never owns page scroll,
 * and must not use the directory list card shell.
 */
export function HubHoverTable<Row>({
  columns,
  rows,
  rowKey,
  caption,
  className,
  tableClassName,
  footer,
}: HubHoverTableProps<Row>): JSX.Element {
  return (
    <div className={cn("w-max max-w-[min(36rem,calc(100vw-2rem))]", className)}>
      <table
        className={cn(
          "w-max max-w-full border-collapse text-left text-[11px] leading-snug",
          tableClassName,
        )}
      >
        {caption != null ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cn(
                  "py-1 font-medium",
                  column.align === "right" ? "px-2 text-right" : "pr-2 text-left",
                  column.headerClassName,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-b border-slate-100 text-slate-800 last:border-b-0">
              {columns.map((column) => {
                const overflow = column.overflow ?? (column.align === "right" ? "nowrap" : "wrap");
                return (
                  <td
                    key={column.id}
                    className={cn(
                      "py-1 align-top",
                      column.align === "right" ? "px-2 text-right tabular-nums" : "pr-2",
                      overflow === "nowrap" ? "whitespace-nowrap" : "max-w-[11rem] break-words",
                      column.cellClassName,
                    )}
                  >
                    {column.cell(row)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  );
}
